/**
 * ч.1427: підпис акта клієнтом. Файл акта приходить із сервера, підпис створюється у браузері ключем
 * із кодом платника (ключ не покидає браузер), далі акт іде у вхідні ТОВ «Ейзі Україна».
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { billing, fmtDate, fmtUah, type BillingAct } from '@/api/billing';
import BodyPortal from '@/shared/components/BodyPortal';
import KeyAuthPanel from '@/shared/eusign/KeyAuthPanel';
import { bytesToBase64, loadEuSign, pickKeyForCode, signDataInternalCadesT } from '@/shared/eusign/eusign';

function errText(e: unknown): string {
  const d = (e as AxiosError<{ detail?: unknown }>)?.response?.data?.detail;
  return typeof d === 'string' ? d : ((e as { message?: string })?.message ?? 'Не вдалося підписати');
}

export default function BillingActSignDialog({ act, onClose, onSigned }: {
  act: BillingAct; onClose: () => void; onSigned: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const sign = useMutation({
    mutationFn: async () => {
      await loadEuSign();
      await pickKeyForCode(act.payer_code ?? '', act.payer_name);
      const blob = await billing.actBlob(act.id);
      const { signature } = await signDataInternalCadesT(new Uint8Array(await blob.arrayBuffer()));
      return billing.signAct(act.id, bytesToBase64(signature));
    },
    onSuccess: onSigned,
    onError: (e) => setError(errText(e)),
  });
  const name = `akt_${act.number}.pdf`.replace(/[/ ]/g, '-');
  return (
    <BodyPortal>
      <div role="dialog" aria-modal="true" aria-label={`Акт № ${act.number}`} onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 1100, display: 'flex',
          justifyContent: 'center', alignItems: 'center', padding: '1rem' }}>
        <div className="card" onClick={(e) => e.stopPropagation()}
          style={{ width: 620, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', margin: 0 }}>
          <h3 style={{ margin: '0 0 0.3rem' }}>Акт № {act.number}</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0 0 0.6rem' }}>
            {act.act_date ? `Дата ${fmtDate(act.act_date)}, ` : ''}сума {fmtUah(act.amount)}, замовник {act.payer_name}.{' '}
            <button type="button" className="btn-link"
              onClick={() => { billing.downloadActPdf(act.id, name).catch((e) => setError(errText(e))); }}>переглянути pdf</button>
          </p>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: '0 0 0.6rem' }}>
            Підпишіть акт ключем КЕП платника{act.payer_code ? ` (код ${act.payer_code})` : ''}: він піде до ТОВ «Ейзі Україна»,
            а підписаний обома сторонами повернеться в журнал ЕДО.
          </p>
          {error && <div className="mc__panel mc__panel--warn" role="alert" style={{ marginBottom: '0.5rem' }}>{error}</div>}
          <KeyAuthPanel />
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.6rem' }}>
            <button onClick={onClose}>закрити</button>
            <button className="primary" disabled={sign.isPending} onClick={() => { setError(null); sign.mutate(); }}>
              {sign.isPending ? 'підписуємо…' : 'підписати і надіслати'}
            </button>
          </div>
        </div>
      </div>
    </BodyPortal>
  );
}
